import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { observer } from 'mobx-react';
import React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { deleteUser } from '../AppShell/Authentication/AuthorisationAsync';
import { GroupIGroup, UserIParentGroup, UserIUser } from '../AppShell/Authentication/AuthorisationModels';
import { formErrorMessage } from '../Authorisation.en';
import { AlertBanner, AlertBannerTypes } from '../Components/AlertBanner';
import { AsyncFetch, AsyncFetchRenderProps } from '../Components/AsyncFetch';
import { ButtonMode, ButtonVariant } from '../Components/Button';
import { StyledDialog } from '../Components/Dialog/StyledDialog';
import { StyledDialogAppBar } from '../Components/Dialog/StyledDialogAppBar';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { CloseIcon, DeleteIcon, PatientInformationIcon, PatientPermissionsIcon } from '../Components/Icons';
import { NavMenu, NavMenuItem } from '../Components/Layout/NavMenu';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { StyledPrompt } from '../Components/Prompts/StyledPrompt';
import { displayMessage } from '../Config/ApplicationConfig';
import { globalIdentifyUser, refreshIdentityUser } from '../Hooks/useIdentityLoader';
import {
    getUserPermissionsRoute,
    getUserRoute,
    routeForNewUser,
    routeForUser,
    routeForUserPermissions,
    routeForUsers,
    UserRouteParams
} from '../Routes';
import { white50, divider } from '../Styles/Colours';
import { UserPermissions } from './Permissions/UserFilePermissions';
import { UserFileInformation } from './UserFileInformation';
import { UserFileStore } from './UserFileStore';

type UserFileProps = RouteComponentProps<UserRouteParams> & {
    onDataChanged(): void;
};

@observer
export class UserFile extends React.Component<UserFileProps> {
    public state = {
        isDeleting: false,
        confirmDeleteDialog: false
    };

    public userFileStore: UserFileStore = new UserFileStore();

    public close = (): void => {
        this.props.history.push(routeForUsers);
    };

    public componentWillUnmount(): void {
        this.props.onDataChanged();
    }

    public saveUserInformation = async (user: UserIUser): Promise<string> => {
        const newUser = this.props.match.params.id === 'new';
        let userId = newUser ? undefined : this.props.match.params.id;

        try {
            userId = await this.userFileStore.saveUserInformation(userId)(user);

            if (globalIdentifyUser !== null && userId === globalIdentifyUser.id) {
                refreshIdentityUser();
            }

            if (userId) {
                if (newUser) {
                    displayMessage('User created');
                } else {
                    displayMessage('User updated');
                }
            }

            return userId;
        } catch (err) {
            if (newUser) {
                console.error('Unable to create user - err: ', err);
                if (err instanceof Error) {
                    this.userFileStore.setFormError(err.message);
                }

                return '';
            } else {
                console.error('Unable to update user: ', err);
                if (err instanceof Error) {
                    this.userFileStore.setFormError(err.message);
                }
                return '';
            }
        }
    };

    public hideDeleteDialog = () => {
        this.setState({ confirmDeleteDialog: false });
    };

    public onDelete = () => {
        this.setState({ confirmDeleteDialog: true });
    };

    public deleteUser = async () => {
        this.setState({ isDeleting: true }, async () => {
            await deleteUser(this.props.match.params.id);
            displayMessage('User deleted');
            this.props.onDataChanged();
        });

        this.setState({ confirmDeleteDialog: false }, () => {
            this.close();
        });
    };

    public sideBarRenderFunction = ({ collapsed }: { collapsed: boolean }): React.ReactNode => {
        const newUser = this.props.match.params.id.toLowerCase() === 'new';

        return (
            <>
                <NavMenu>
                    <NavMenuItem
                        icon={<PatientInformationIcon size='24' noCircularContainer />}
                        exact
                        title={'Account information'}
                        pathname={this.props.location.pathname}
                        location={getUserRoute(this.props.match.params.id)}
                        referrerLocation={this.props.location}
                    />
                    <NavMenuItem
                        icon={
                            <PatientPermissionsIcon
                                size='24'
                                color={newUser ? 'midGrey' : 'grey'}
                                noCircularContainer
                            />
                        }
                        exact
                        disabled={newUser}
                        title={'Permissions'}
                        pathname={this.props.location.pathname}
                        location={getUserPermissionsRoute(this.props.match.params.id)}
                        referrerLocation={this.props.location}
                    />
                </NavMenu>
            </>
        );
    };

    public render(): React.ReactNode {
        const newUser = this.props.match.params.id.toLowerCase() === 'new';

        const contents = (
            <Switch>
                <Route
                    path={routeForNewUser}
                    exact
                    render={({ history }) => (
                        <AsyncFetch
                            key='new-information'
                            fetch={this.userFileStore.loadUserInformation()}
                            render={({
                                data,
                                loading,
                                refresh
                            }: AsyncFetchRenderProps<{
                                user: UserIUser;
                                groups: UserIParentGroup[];
                                systemContact: UserIUser | undefined;
                                defaultGroup: GroupIGroup | undefined;
                            }>) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <UserFileInformation
                                        key='new'
                                        isNew
                                        user={data.user}
                                        systemContact={data.systemContact}
                                        defaultGroup={data.defaultGroup}
                                        groups={data.groups}
                                        isDeleting={this.state.isDeleting}
                                        loadInformation={refresh}
                                        onClose={this.close}
                                        onError={this.userFileStore.setFormError}
                                        history={history}
                                        onSaveUserInformation={this.saveUserInformation}
                                    />
                                );
                            }}
                        />
                    )}
                />
                <Route
                    path={routeForUser}
                    exact
                    render={({ match, history }) => (
                        <AsyncFetch
                            key='information'
                            fetch={this.userFileStore.loadUserInformation(match.params.id)}
                            render={({
                                data,
                                refresh,
                                loading
                            }: AsyncFetchRenderProps<{
                                user: UserIUser;
                                groups: UserIParentGroup[];
                                systemContact: UserIUser | undefined;
                                defaultGroup: GroupIGroup | undefined;
                            }>) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <UserFileInformation
                                        key={match.params.id}
                                        user={data.user}
                                        systemContact={data.systemContact}
                                        defaultGroup={data.defaultGroup}
                                        groups={data.groups}
                                        isDeleting={this.state.isDeleting}
                                        loadInformation={refresh}
                                        onClose={this.close}
                                        onError={this.userFileStore.setFormError}
                                        history={history}
                                        onSaveUserInformation={this.saveUserInformation}
                                    />
                                );
                            }}
                        />
                    )}
                />
                <Route
                    path={routeForUserPermissions}
                    exact
                    render={({ match }) => (
                        <AsyncFetch
                            key='permissions'
                            fetch={this.userFileStore.loadUserPermissions(match.params.id)}
                            renderError={() => {
                                return <div>Error loading permissions</div>;
                            }}
                            render={({ data, refresh, loading }) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <UserPermissions
                                        permissions={data}
                                        isDeleting={this.state.isDeleting}
                                        loadPermissions={refresh}
                                        onClose={this.close}
                                        onError={this.userFileStore.setFormError}
                                        onUpdatePermissions={permissions =>
                                            this.userFileStore.updatePermissions(match.params.id, permissions)
                                        }
                                    />
                                );
                            }}
                        />
                    )}
                />
            </Switch>
        );

        const alerts = this.userFileStore.formError && (
            <AlertBanner promptMessage={formErrorMessage} promptType={AlertBannerTypes.Error} />
        );

        const actionButtons = () => {
            const closeIcon = (
                <>
                    <Tooltip id='close-icon' title='Close'>
                        <IconButton onClick={this.close}>
                            <CloseIcon color={white50} />
                        </IconButton>
                    </Tooltip>
                </>
            );

            if (this.props.match.params.id === 'new') return closeIcon;

            return (
                <>
                    <Tooltip title='Delete user'>
                        <IconButton onClick={this.onDelete}>
                            <DeleteIcon color={white50} />
                        </IconButton>
                    </Tooltip>
                    {closeIcon}
                </>
            );
        };

        const header = (
            <StyledDialogAppBar title={newUser ? 'Create user' : 'User details'} titleBarRight={actionButtons()} />
        );

        return (
            <StyledDialog
                open
                modal
                size='lg'
                titleComponent={header}
                contentComponent={
                    <>
                        {contents}
                        <StyledPrompt
                            open={this.state.confirmDeleteDialog}
                            title={'Delete user'}
                            onCancel={this.hideDeleteDialog}
                            actions={
                                <>
                                    <StyledDialogButton
                                        mode={ButtonMode.border}
                                        testId='cancel'
                                        disabled={this.state.isDeleting}
                                        onClick={this.hideDeleteDialog}
                                        borderColour={divider}
                                    >
                                        Cancel
                                    </StyledDialogButton>
                                    <StyledDialogButton
                                        variant={ButtonVariant.danger}
                                        testId='delete'
                                        disabled={this.state.isDeleting}
                                        onClick={this.deleteUser}
                                    >
                                        Delete
                                    </StyledDialogButton>
                                </>
                            }
                        >
                            Are you sure you wish to remove this user?
                        </StyledPrompt>
                    </>
                }
                alertContent={alerts}
                sidebarRender={this.sideBarRenderFunction}
            />
        );
    }
}
